import React, { useState, useEffect } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Paper, Stack, Typography, Box } from '@mui/material'
import dayjs from 'dayjs'
import * as yup from 'yup'

import { Form, useForm } from '@cedarjs/forms'
import { toast } from 'react-hot-toast'

import { useOrgName } from 'src/hooks/useOrgName'
import { roundToPrecision } from 'src/utils/helpers'
import PrimSecTextCombo from '../common/PrimSecTextCombo'
import Button from '../MUI/Button'
import Checkbox from '../MUI/Checkbox'
import DatePicker from '../MUI/DatePickerFormatted'
import NumericAccruedTextField from '../NumericAccruedTextField/NumericAccruedTextField'

interface CreateTimeFormProps {
  aircraft: {
    id: string
    tailNumber: string
    model: string
    components: Array<{
      id: string
      name: string
      partNumber?: string
      serialNumber?: string
    }>
  }
  onSuccess?: (usageLogId: string) => void
}

const GET_LATEST_USAGE_LOGS = gql`
  query GetLatestExternalAircraftUsageLogs(
    $orgSlug: String!
    $externalAircraftId: String!
    $beforeDate: DateTime!
  ) {
    externalAircraftUsageLogs(
      orgSlug: $orgSlug
      filter: {
        externalAircraftId: $externalAircraftId
        dateRange: { startDate: "1900-01-01T00:00:00Z", endDate: $beforeDate }
      }
      pagination: { first: 1 }
    ) {
      edges {
        node {
          id
          usageAsOf
          ExternalComponentUsageLog {
            id
            externalComponentId
            hours
            cycles
            component {
              id
              name
            }
          }
        }
      }
    }
  }
`

const CREATE_EXTERNAL_AIRCRAFT_USAGE_LOG = gql`
  mutation CreateExternalAircraftUsageLog(
    $input: CreateExternalAircraftUsageLogInput!
  ) {
    createExternalAircraftUsageLog(input: $input) {
      id
      externalAircraftId
      usageAsOf
      createdAt
      ExternalComponentUsageLog {
        id
        externalComponentId
        hours
        cycles
        component {
          id
          name
        }
      }
    }
  }
`

// Simplified validation schema
const schema = yup.object({
  usageAsOf: yup.string().required('Usage date is required'),
})

const CreateTimeForm: React.FC<CreateTimeFormProps> = ({
  aircraft,
  onSuccess,
}) => {
  const orgSlug = useOrgName()
  const [isAccruedMode] = useState(false) // Always total mode for now

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      usageAsOf: dayjs().startOf('day').format('YYYY-MM-DD'),
    },
  })

  const { handleSubmit, reset, watch } = formMethods
  const watchedDate = watch('usageAsOf')

  // Fetch latest usage logs for pre-population based on selected date
  const {
    data: usageLogsData,
    loading: usageLogsLoading,
    refetch: refetchUsageLogs,
  } = useQuery(GET_LATEST_USAGE_LOGS, {
    variables: {
      orgSlug,
      externalAircraftId: aircraft.id,
      beforeDate: watchedDate ? dayjs(watchedDate).toISOString() : undefined,
    },
    skip: !orgSlug || !aircraft.id || !watchedDate,
  })

  // Watch for date changes and refetch usage logs
  useEffect(() => {
    if (watchedDate && orgSlug && aircraft.id) {
      refetchUsageLogs({
        orgSlug,
        externalAircraftId: aircraft.id,
        beforeDate: dayjs(watchedDate).toISOString(),
      })
    }
  }, [watchedDate, orgSlug, aircraft.id, refetchUsageLogs])

  // Pre-populate form with latest usage data
  useEffect(() => {
    if (usageLogsData?.externalAircraftUsageLogs?.edges?.length > 0) {
      const latestUsageLog =
        usageLogsData.externalAircraftUsageLogs.edges[0].node
      const componentUsageLogs = latestUsageLog.ExternalComponentUsageLog || []

      // Create a map of component ID to latest usage data
      const componentUsageMap = new Map()
      componentUsageLogs.forEach((log) => {
        componentUsageMap.set(log.externalComponentId, {
          hours: log.hours || 0,
          cycles: log.cycles || 0,
        })
      })

      // Build component values only (preserve current date and notes)
      const componentValues: Record<string, number> = {}

      // Set values for each component
      aircraft.components.forEach((component) => {
        const fields = getComponentFields(component)
        const usage = componentUsageMap.get(component.id) || {
          hours: 0,
          cycles: 0,
        }

        componentValues[fields.hours] = usage.hours
        componentValues[fields.cycles] = usage.cycles
      })

      // Reset only component fields, preserve date
      reset((prevValues) => ({
        ...prevValues,
        ...componentValues,
      }))
    } else {
      // No previous usage logs - set component fields to 0
      const componentValues: Record<string, number> = {}

      aircraft.components.forEach((component) => {
        const fields = getComponentFields(component)
        componentValues[fields.hours] = 0
        componentValues[fields.cycles] = 0
      })

      // Reset only component fields, preserve date
      reset((prevValues) => ({
        ...prevValues,
        ...componentValues,
      }))
    }
  }, [usageLogsData, aircraft.components, reset])

  const [createUsageLogMutation, { loading: saveLoading }] = useMutation(
    CREATE_EXTERNAL_AIRCRAFT_USAGE_LOG,
    {
      onCompleted: (data) => {
        if (data.createExternalAircraftUsageLog) {
          onSuccess?.(data.createExternalAircraftUsageLog.id)
        }
      },
      onError: (error) => {
        toast.error(`Failed to update aircraft times: ${error.message}`)
      },
    }
  )

  // Helper function to determine component type
  const getComponentType = (componentName: string) => {
    const name = componentName.toLowerCase()
    if (
      name.includes('airframe') ||
      name.includes('fuselage') ||
      name.includes('wing')
    ) {
      return 'airframe'
    }
    if (name.includes('engine')) {
      return 'engine'
    }
    if (name.includes('apu') || name.includes('auxiliary power')) {
      return 'apu'
    }
    return 'component'
  }

  // Helper function to get field names for a component
  const getComponentFields = (component) => {
    const componentType = getComponentType(component.name)
    const baseId = component.id

    if (componentType === 'airframe') {
      return {
        hours: `${baseId}_airframeHours`,
        cycles: `${baseId}_airframeLandings`,
        cyclesLabel: 'Landings',
      }
    } else if (componentType === 'engine') {
      return {
        hours: `${baseId}_engineHours`,
        cycles: `${baseId}_engineCycles`,
        cyclesLabel: 'Cycles',
      }
    } else if (componentType === 'apu') {
      return {
        hours: `${baseId}_apuHours`,
        cycles: `${baseId}_apuCycles`,
        cyclesLabel: 'Cycles',
      }
    } else {
      return {
        hours: `${baseId}_hours`,
        cycles: `${baseId}_cycles`,
        cyclesLabel: 'Cycles',
      }
    }
  }

  const onSubmit = async (data) => {
    try {
      // Collect component data for each component
      const components = aircraft.components
        .map((component) => {
          const fields = getComponentFields(component)
          const hours = parseFloat(data[fields.hours] || '0')
          const cycles = parseFloat(data[fields.cycles] || '0')

          // Only include components that have hours or cycles data
          if (hours > 0 || cycles > 0) {
            return {
              externalComponentId: component.id,
              hours: hours > 0 ? hours : null,
              cycles: cycles > 0 ? cycles : null,
            }
          }
          return null
        })
        .filter(Boolean) // Remove null entries

      if (components.length === 0) {
        toast.error('Please enter hours or cycles for at least one component')
        return
      }

      const input = {
        orgSlug,
        externalAircraftId: aircraft.id,
        usageAsOf: dayjs(data.usageAsOf).toISOString(),
        components,
      }

      await createUsageLogMutation({ variables: { input } })
    } catch (error) {
      console.error('Error creating usage log:', error)
    }
  }

  if (usageLogsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography>Loading latest usage data...</Typography>
      </Box>
    )
  }

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ p: 3 }}>
        {/* Aircraft Info Header */}
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Update the usage data for the aircraft{' '}
            <strong>{aircraft.tailNumber}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last log provided on: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Component Fields */}
        <Stack spacing={2}>
          {aircraft.components.map((component, index) => {
            const componentType = getComponentType(component.name)
            const fields = getComponentFields(component)
            const isFirstComponent = index === 0

            return (
              <Box key={component.id}>
                {/* Part Number */}
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {component.partNumber || component.name}
                </Typography>

                {/* Component Type - Serial Number */}
                <Typography variant="body1" gutterBottom>
                  {componentType === 'airframe'
                    ? 'Airframe'
                    : componentType === 'engine'
                    ? `Engine ${Math.floor(index / 2) + 1}`
                    : componentType === 'apu'
                    ? 'APU'
                    : 'Component'}{' '}
                  - {component.serialNumber || 'N/A'}
                </Typography>

                {/* Input Fields Grid - using same layout as original */}
                <div className="grid grid-cols-9 gap-1">
                  {/* Date field - only for first component */}
                  {isFirstComponent ? (
                    <div className="col-span-3">
                      <DatePicker
                        key={watchedDate || 'default'} // Force re-render when date changes
                        name="usageAsOf"
                        label="Date"
                        required
                        maxDate={dayjs()}
                        format="MM/DD/YYYY"
                        inputFormat="MM/DD/YYYY"
                        slotProps={{
                          field: {
                            clearable: true,
                          },
                          textField: {
                            placeholder: 'MM/DD/YYYY',
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="col-span-3" />
                  )}

                  {/* Hours field */}
                  <div className="col-span-3">
                    <NumericAccruedTextField
                      name={fields.hours}
                      label="Hours"
                      isAccruedMode={isAccruedMode}
                      precision={1}
                    />
                  </div>

                  {/* Landings/Cycles field */}
                  <div className="col-span-3">
                    <NumericAccruedTextField
                      name={fields.cycles}
                      label={fields.cyclesLabel}
                      isAccruedMode={isAccruedMode}
                      precision={0}
                    />
                  </div>
                </div>
              </Box>
            )
          })}
        </Stack>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => formMethods.reset()}
          >
            Reset
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={saveLoading}
          >
            Apply Times
          </LoadingButton>
        </Box>
      </Stack>
    </Form>
  )
}

export default CreateTimeForm
